import { useGetModulesWithAgendasQuery } from 'store/services/core/modules';
import { memo, useCallback } from 'react';
import { IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { veoliaIcon, flowioIcon, scvIcon, csadIcon } from 'assets/svgIcons';
import { UserMenu } from './UserMenu';
import { AdminMenu } from './AdminMenu';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMobileApp } from 'utils/common';
import { useLogin } from 'contexts';
import { useDispatch, useSelector } from 'react-redux';
import { setOpenMenu, closeMenu } from 'store/entities/dashboard/actions';
import { selectMenuOpened } from 'store/entities/dashboard/selectors';
import { SearchBar } from '../Header/SearchBar';

import { DATA_STRUCTURE } from 'types/enums';
import { useNotificationRefresh } from 'utils/hooks';
import { MenuNavigationContext } from './menuNavigationContext';
import { useMenuDrawerNavigation } from './useMenuDrawerNavigation';
import { DesktopDrawer, Header, MenuWrapper, SearchBarWrapper, ToggleButton } from './index.styles';

export const Menu = memo(() => {
  const mobileApp = isMobileApp();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useLogin();
  const open = useSelector(selectMenuOpened());
  const handleToggleMenu = useCallback(() => dispatch(setOpenMenu()), [dispatch]);
  const handleCloseMenu = useCallback(() => dispatch(closeMenu()), [dispatch]);
  const isUserMenu = !(isAdmin && pathname.includes('admin'));
  const { navigateFromMenu, onDrawerExited } = useMenuDrawerNavigation();

  const { refetch: refetchModuleNotifications } = useGetModulesWithAgendasQuery(
    { takeAgendas: 0, takeModules: 0, dataStructure: DATA_STRUCTURE.HIERARCHICAL },
    { skip: !isUserMenu }
  );
  useNotificationRefresh(refetchModuleNotifications);

  const MenuComponent = isUserMenu ? UserMenu : AdminMenu;

  const logoName = process.env.REACT_APP_LOGO_NAME;
  const logoIcon = (() => {
    switch (logoName) {
      case 'veolia':
        return veoliaIcon;
      case 'flowio':
        return flowioIcon;
      case 'scvIcon':
        return scvIcon;
      case 'csadIcon':
        return csadIcon;
      default:
        return flowioIcon;
    }
  })();

  const handleLogoClick = useCallback(() => {
    navigate(isAdmin && pathname.includes('admin') ? '/admin' : '/user');
  }, [navigate, isAdmin, pathname]);

  return (
    <DesktopDrawer
      drawerOpen={open}
      open={open}
      variant={mobileApp ? 'temporary' : 'permanent'}
      onClose={handleCloseMenu}
      slotProps={{ transition: { onExited: onDrawerExited } }}>
      <Header open={open} onClick={handleLogoClick} data-testid="sidebar-header">
        <img src={logoIcon} alt="logo" data-testid="sidebar-logo" />
        <Typography variant="h5" data-testid="sidebar-logo-text">
          {process.env.REACT_APP_LOGO_DESCRIPTION}
        </Typography>
        {(!mobileApp || open) && (
          <ToggleButton open={open}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                (mobileApp ? handleCloseMenu : handleToggleMenu)();
              }}
              size="small"
              data-testid="sidebar-toggle-button"
              aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
              disableRipple>
              {open ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          </ToggleButton>
        )}
      </Header>

      {open && (
        <SearchBarWrapper>
          <SearchBar />
        </SearchBarWrapper>
      )}

      <MenuWrapper>
        <MenuNavigationContext.Provider value={navigateFromMenu}>
          <MenuComponent open={open} />
        </MenuNavigationContext.Provider>
      </MenuWrapper>
    </DesktopDrawer>
  );
});
